import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import { PageWrapper } from '../../../CommonComponents/PageWrapper/PageWrapper';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { MySettingsDTO, useMySettingsQuery } from './api/useMySettingsQuery';
import {
  FormValues,
  parseDefaultValues,
  parseSubmittedValues,
  validation,
} from './MySettings.meta';
import { useSaveMySettingsMutation } from './api/useSaveMySettingsMutation';
import { ContactInfo } from './components/ContactInfo/ContactInfo';
import { CustomSignature } from './components/CustomSignature/CustomSignature';
import { FirmLogo } from '../FirmSettings/components/FirmLogo/FirmLogo';
import { CustomDisclosure } from '../FirmSettings/components/CustomDisclosure/CustomDisclosure';
import { TermsAndConditions } from '../FirmSettings/components/TermsAndConditions/TermsAndConditions';
import { Assumptions } from '../FirmSettings/components/Assumptions/Assumptions';
import { ModelGroup } from './components/ModelGroup/ModelGroup';
import { AutoAccountsAgreements } from '../FirmSettings/components/AutoAccountsAgreements/AutoAccountsAgreements';
import { showOPLConfirmationDialog } from '../../../helpers/showOPLConfirmationDialog';
import { FullPageLoading } from '../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { AssetBucketing } from './components/AssetBucketing/AssetBucketing';
import { ManualAccounts } from '../FirmSettings/components/ManualAccounts/ManualAccounts';
import { invariant } from '../../../CommonUtils/invariant';
import { BeFi20 } from './components/BeFi20/BeFi20';
import { getApplicationVariables } from '../../../AdviserArea/AdviserAreaActions';
import { PulseCheck } from './components/PulseCheck/PulseCheck';
import { MobileAppNotifications } from '../FirmSettings/components/MobileAppNotifications/MobileAppNotifications';
import { CurrentYearAssumptionSetting } from '../../../CommonComponents/CurrentYearAssumptionSetting/CurrentYearAssumptionSetting';
import { DocumentVault } from './components/DocumentVault/DocumentVault';

export const MySettings = () => {
  const { isLoading, isError, refetch, data } = useMySettingsQuery();

  if (isLoading) {
    return <FullPageLoading />;
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" my="56">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  invariant(data, `[MySettings] Query \`data\` is ${data}`);

  return (
    <PageWrapper>
      <MySettingsForm mySettings={data} />
    </PageWrapper>
  );
};

interface Props {
  mySettings: MySettingsDTO;
}

const MySettingsForm: React.FC<Props> = ({ mySettings }) => {
  const [showCurrentYearAssumptionSetting, setCurrentYearAssumptionSettingVisibility] = useState(
    false,
  );

  const dispatch = useDispatch();

  const formBag = useForm<FormValues>({
    defaultValues: parseDefaultValues(mySettings),
    resolver: yupResolver(validation),
  });

  const {
    mutateAsync: saveMySettings,
    isLoading: isSavingMySettings,
    error: saveMySettingsError,
  } = useSaveMySettingsMutation();

  useEffect(() => {
    if (isEmpty(formBag.formState.dirtyFields)) {
      ADV.util.Router.unregisterNavigateAwayCallback();
    } else {
      ADV.util.Router.registerNavigateAwayCallback(showOPLConfirmationDialog);
    }
  }, [formBag.formState]);

  const onSubmit = async (values: FormValues) => {
    try {
      await saveMySettings(parseSubmittedValues(values, mySettings));

      // After a successfully submit, clear all dirty fields
      formBag.reset(values);

      // Request app variables in order to know if advisor has access to asset bucketing layout
      dispatch(getApplicationVariables());

      toast.success('Your settings were successfully saved');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Box as="form" mt="24" onSubmit={formBag.handleSubmit(onSubmit)}>
        {saveMySettingsError && (
          <Alert message={saveMySettingsError.message} variant="error" mb="16" />
        )}

        <ContactInfo formBag={formBag} mb="56" />

        {mySettings.allowAdvisersOverrideAgreements && (
          <AutoAccountsAgreements agreements={mySettings.agreements} formBag={formBag} mb="56" />
        )}

        <CustomSignature formBag={formBag} mb="56" />

        {mySettings.allowAdvisersOverrideFirmLogo && (
          <FirmLogo
            fieldName="adviserFirmLogoFilename"
            formBag={formBag}
            customFirmUrl={mySettings.customFirmUrl}
            mb="56"
          />
        )}

        {mySettings.customDisclosuresEditableByAdviser && (
          <CustomDisclosure fieldName="adviserCustomDisclosures" formBag={formBag} mb="56" />
        )}

        {mySettings.allowAdvisersCustomTermsAndConditions && (
          <TermsAndConditions fieldName="customTermsAndConditions" formBag={formBag} mb="56" />
        )}

        <Assumptions
          isReadonly={!mySettings.assumptionsEditableByAdviser}
          firmAssumptions={mySettings.firmAssumptions}
          formBag={formBag}
          is3dRiskToleranceWorkflowEnabled={mySettings.is3dRiskToleranceWorkflowEnabled}
          isCustomAssumptionsEnabled={mySettings.showCmaCustomAssumptions}
          mb="56"
          onOpenCurrentYearAssumptionsModal={() => setCurrentYearAssumptionSettingVisibility(true)}
        />

        <ModelGroup formBag={formBag} groups={mySettings.portfolioGroups} mb="56" />

        {mySettings.allowAssetBucketingFirm && (
          <AssetBucketing
            formBag={formBag}
            mb="56"
            allowClientsToCustomize={
              mySettings.allowAdviserToOverrideClientAssetBucketingCustomization
            }
          />
        )}

        {mySettings.allowAdviserOverrideAddManualAccount && (
          <ManualAccounts fieldName="allowAddManualAccount" formBag={formBag} mb="56" />
        )}

        {mySettings.allowBeFi20Firm && <BeFi20 formBag={formBag} mb="56" />}

        {mySettings.allowPulseCheckFirm && <PulseCheck formBag={formBag} mb="56" />}

        {mySettings.allowMobileAppPushNotificationsFirm && (
          <MobileAppNotifications
            formBag={formBag}
            mb="56"
            allowDocumentVaultPushNotifications={mySettings.hasDocumentVaultFirm}
          />
        )}

        <DocumentVault formBag={formBag} mb="56" />

        <Box display="flex" justifyContent="flex-end" mb="32">
          <Button type="submit" size="large" isLoading={isSavingMySettings}>
            Save
          </Button>
        </Box>
      </Box>

      {showCurrentYearAssumptionSetting && (
        <CurrentYearAssumptionSetting
          onClose={() => {
            setCurrentYearAssumptionSettingVisibility(false);
          }}
          isReadonly={!mySettings.assumptionsEditableByAdviser}
        />
      )}
    </>
  );
};
